/**
 * Pure helpers for the Assistant's screen context (CU-14zefhy0dgu, contract E/F/G): what the user is looking at,
 * sent with each chat message so the CoPilot can answer about "this order" / "why can't I save".
 *
 * Privacy rules (task owner decision): no form field VALUES ever — only the names of changed fields and validation
 * errors; never anything from password / token / secret / pin / card / cvv / iban-like fields; every string is cut
 * to 200 characters; the whole payload is capped at 4 KB. When the assistant needs to see more it asks for a
 * screenshot, which the user has to allow.
 *
 * No Angular imports here so the helpers can be unit-tested without a TestBed.
 */

export type AssistantScreenKind = 'list' | 'view' | 'edit' | 'create' | 'other';
export type AssistantTrailType = 'nav' | 'click' | 'dialog' | 'apiError' | 'toast';

export interface AssistantTrailEntry {
    t: AssistantTrailType;
    /** ISO timestamp */
    at: string;
    label: string;
    detail?: string;
}

export interface AssistantListContext {
    filters?: {[column: string]: string};
    search?: string;
    sort?: string;
    page?: number;
    selectedIds?: string[];
}

export interface AssistantFormContext {
    /** names (paths) of the fields the user changed — never their values */
    changed?: string[];
    errors?: Array<{field: string; message: string}>;
    /** LABELS of required fields that are still empty (attached context only; CU-14zefhy1qnh) */
    requiredEmpty?: string[];
    /** field errors of the last 400 answer on this screen */
    apiFieldErrors?: Array<{field: string; message: string}>;
    /** stepper / wizard position, 1-based */
    step?: {index: number; count: number; title?: string};
}

/** Attached-context diagnostics (CU-14zefhy1qnh): only ever sent inside a context the user attached with 🧭. */
export interface AssistantConsoleError {
    at: string;
    message: string;
    /** "file.js:12:5" (no query string) */
    source?: string;
}

export interface AssistantFailedRequest {
    at: string;
    method: string;
    path: string;
    /** 0 = network error / CORS / timeout */
    status: number;
    message?: string;
    durationMs?: number;
}

export interface AssistantAccess {
    role?: string;
    /** role-setting keys this screen evaluated while rendering → result */
    checks?: {[key: string]: boolean};
    disabledActions?: Array<{label: string; reason?: string}>;
    hiddenActions?: string[];
}

export interface AssistantEnv {
    appVersion?: string;
    browser?: string;
    os?: string;
    screen?: string;
    viewport?: string;
    locale?: string;
    timeZone?: string;
    dateFormat?: string;
    online?: boolean;
    slowRequests?: Array<{method: string; path: string; ms: number}>;
}

export interface AssistantScreen {
    kind: AssistantScreenKind;
    entity?: string;
    id?: string;
    list?: AssistantListContext;
    form?: AssistantFormContext;
}

export interface AssistantScreenContext {
    url: string;
    title?: string;
    breadcrumb?: string[];
    screen: AssistantScreen;
    trail?: AssistantTrailEntry[];
    /** real in-app paths (`routesHint`), sent with the first message of a chat so replies link correctly */
    routes?: string[];
    /** pill text of an attached context ("Haulier Orders · list (2 selected)"), sent in `contexts[]` (CU-14zefhy1qnh) */
    label?: string;
    // ----- attached context only (CU-14zefhy1qnh), all optional and redacted -----
    consoleErrors?: AssistantConsoleError[];
    failedRequests?: AssistantFailedRequest[];
    access?: AssistantAccess;
    /** error dialogs and error toasts open on screen */
    visibleErrors?: string[];
    /** a list showing "no data", with the filters / search that produced it */
    emptyState?: {text?: string; filters?: {[column: string]: string}; search?: string};
    env?: AssistantEnv;
    /** tenant settings this screen read (non-secret; "[hidden]" otherwise) */
    settings?: {[key: string]: string};
}

export const CONTEXT_MAX_BYTES = 4096;
export const TRAIL_MAX = 15;
export const VALUE_MAX = 200;
export const SELECTED_IDS_MAX = 50;
const FORM_NAMES_MAX = 30;

const SENSITIVE_WORDS = new Set(['password', 'passwd', 'pwd', 'pass', 'token', 'secret', 'pin', 'cvv', 'cvc', 'card', 'iban', 'otp']);
const SENSITIVE_JOINED = /(password|passcode|secret|token|apikey|iban|cardnumber|cardno|sortcode|accountnumber|accountno|bankaccount)/;

/** True for a field / column / attribute name that may hold a credential or payment detail. */
export function isSensitiveName(name: string): boolean {
    if (!name) {
        return false;
    }
    const words = String(name)
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean);
    if (words.some((w) => SENSITIVE_WORDS.has(w))) {
        return true;
    }
    return SENSITIVE_JOINED.test(words.join(''));
}

/** Collapses whitespace and cuts to `max` characters (with an ellipsis). */
export function clip(value: any, max = VALUE_MAX): string {
    if (value === null || value === undefined) {
        return '';
    }
    const s = String(value).replace(/\s+/g, ' ').trim();
    if (s.length <= max) {
        return s;
    }
    let cut = s.substring(0, max - 1);
    // RMA-54: never leave half of a surrogate pair (an emoji) in front of the ellipsis
    const last = cut.charCodeAt(cut.length - 1);
    if (last >= 0xd800 && last <= 0xdbff) {
        cut = cut.substring(0, cut.length - 1);
    }
    return cut + '…';
}

/** Path part of a URL: no origin, no query string, no fragment. */
export function pathOnly(url: string): string {
    if (!url) {
        return '/';
    }
    let s = String(url);
    const proto = s.indexOf('://');
    if (proto >= 0) {
        const slash = s.indexOf('/', proto + 3);
        s = slash >= 0 ? s.substring(slash) : '/';
    }
    s = s.split('#')[0].split('?')[0].split(';')[0];
    return s || '/';
}

/** "haulierOrders" / "haulier-orders" → "Haulier Orders". */
export function humanize(name: string): string {
    if (!name) {
        return '';
    }
    return String(name)
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[-_]+/g, ' ')
        .trim()
        .split(/\s+/)
        .map((w) => w.charAt(0).toUpperCase() + w.substring(1))
        .join(' ');
}

/** "Haulier Orders" → "Haulier Order" (good enough for labels). */
export function singular(label: string): string {
    if (/ies$/i.test(label)) {
        return label.replace(/ies$/i, 'y');
    }
    if (/(ss|us)$/i.test(label)) {
        return label;
    }
    if (/(sses|xes|ches|shes)$/i.test(label)) {
        return label.replace(/es$/i, '');
    }
    return label.replace(/s$/i, '');
}

/** Breadcrumb from the route: "/orders/haulier-orders/123/edit" → ["Orders", "Haulier Orders", "#123", "Edit"]. */
export function breadcrumbFromUrl(url: string, entityLabel?: string): string[] {
    const parts = pathOnly(url)
        .split('/')
        .filter(Boolean)
        .slice(0, 6)
        .map((p) => {
            let seg = p;
            try {
                seg = decodeURIComponent(p);
            } catch (e) {
                // keep raw segment
            }
            return /^\d+$/.test(seg) ? '#' + seg : clip(humanize(seg), 40);
        });
    if (entityLabel) {
        const label = clip(entityLabel, 60);
        const idIdx = parts.findIndex((p) => p.startsWith('#'));
        if (idIdx >= 0) {
            parts[idIdx] = label;
        } else {
            parts.push(label);
        }
    }
    return parts;
}

/** Validation errors of one control as readable text — never echoes the value (e.g. `pattern.actualValue`). */
export function errorText(errors: {[key: string]: any}): string {
    if (!errors) {
        return '';
    }
    const texts = Object.keys(errors).map((key) => {
        const e = errors[key];
        switch (key) {
            case 'required':
                return 'required';
            case 'email':
                return 'not a valid email';
            case 'pattern':
                return 'wrong format';
            case 'min':
                return e && e.min !== undefined ? `must be at least ${e.min}` : 'too small';
            case 'max':
                return e && e.max !== undefined ? `must be at most ${e.max}` : 'too large';
            case 'minlength':
                return e && e.requiredLength !== undefined ? `needs at least ${e.requiredLength} characters` : 'too short';
            case 'maxlength':
                return e && e.requiredLength !== undefined ? `at most ${e.requiredLength} characters` : 'too long';
            default:
                // custom validators often carry a message string; anything else → just the key
                return typeof e === 'string' ? clip(e, 120) : e && typeof e.message === 'string' ? clip(e.message, 120) : humanize(key).toLowerCase();
        }
    });
    return texts.join(', ');
}

/** Minimal shape of an Angular AbstractControl / FormGroup / FormArray (duck-typed so tests need no Angular). */
export interface FormLike {
    controls?: {[key: string]: FormLike} | FormLike[];
    dirty?: boolean;
    disabled?: boolean;
    errors?: {[key: string]: any} | null;
}

/**
 * Names of changed fields + validation errors of a form. Values are never read. Sensitive fields are skipped
 * entirely (name and error).
 */
export function formSummary(form: FormLike): AssistantFormContext | undefined {
    if (!form) {
        return undefined;
    }
    const changed: string[] = [];
    const errors: Array<{field: string; message: string}> = [];
    const walk = (control: FormLike, path: string, depth: number) => {
        if (!control || control.disabled || depth > 6) {
            return;
        }
        const name = path.split('.').pop();
        if (path && isSensitiveName(name)) {
            return;
        }
        const children = control.controls;
        const hasChildren = children && (Array.isArray(children) ? children.length > 0 : Object.keys(children).length > 0);
        if (path && control.errors) {
            // group-level errors (cross-field validators) and leaf errors
            if (errors.length < FORM_NAMES_MAX) {
                errors.push({field: path, message: errorText(control.errors)});
            }
        }
        if (hasChildren) {
            if (Array.isArray(children)) {
                children.forEach((c, i) => walk(c, path ? `${path}.${i}` : String(i), depth + 1));
            } else {
                Object.keys(children).forEach((k) => walk(children[k], path ? `${path}.${k}` : k, depth + 1));
            }
        } else if (path && control.dirty && changed.length < FORM_NAMES_MAX) {
            changed.push(path);
        }
    };
    walk(form, '', 0);
    if (form.errors && errors.length < FORM_NAMES_MAX) {
        errors.unshift({field: '(form)', message: errorText(form.errors)});
    }
    if (!changed.length && !errors.length) {
        return undefined;
    }
    const out: AssistantFormContext = {};
    if (changed.length) {
        out.changed = changed;
    }
    if (errors.length) {
        out.errors = errors;
    }
    return out;
}

/** Minimal shape of the NGXS list view slice (`state.entities[x].listView`). */
export interface ListViewLike {
    viewParams?: {
        pageIndex?: number;
        sortColumn?: string;
        sortDirection?: string;
        filter?: string;
        filterParams?: Array<{columnName: string; currentValue: any; extra?: any}> | null;
    };
    selectedIds?: any[];
}

function filterValueText(value: any, extra: any): string {
    if (extra && typeof extra === 'object') {
        // search filters keep the shown label in `extra` (e.g. {name: 'Northway Foods'}); prefer it over a raw id
        const shown = Object.keys(extra)
            .filter((k) => !isSensitiveName(k))
            .map((k) => extra[k])
            .filter((v) => v !== null && v !== undefined && typeof v !== 'object');
        if (shown.length) {
            return clip(shown.join(' '));
        }
    }
    if (Array.isArray(value)) {
        return clip(value.map((v) => (v && typeof v === 'object' ? v.name || v.value || v.id : v)).join(', '));
    }
    if (value && typeof value === 'object') {
        return clip(value.name || value.label || value.id || '');
    }
    return clip(value);
}

/** Filters, search, sort, page and selected rows of a list screen. */
export function listSummary(listView: ListViewLike): AssistantListContext | undefined {
    if (!listView) {
        return undefined;
    }
    const out: AssistantListContext = {};
    const vp = listView.viewParams;
    if (vp) {
        const filters: {[column: string]: string} = {};
        (vp.filterParams || []).forEach((f) => {
            if (!f || !f.columnName || isSensitiveName(f.columnName)) {
                return;
            }
            const v = filterValueText(f.currentValue, f.extra);
            if (v) {
                filters[f.columnName] = v;
            }
        });
        if (Object.keys(filters).length) {
            out.filters = filters;
        }
        if (vp.filter) {
            out.search = clip(vp.filter);
        }
        if (vp.sortColumn) {
            out.sort = vp.sortColumn + (vp.sortDirection ? ',' + vp.sortDirection : '');
        }
        if (typeof vp.pageIndex === 'number') {
            out.page = vp.pageIndex;
        }
    }
    const ids = (listView.selectedIds || []).slice(0, SELECTED_IDS_MAX).map((id) => clip(id, 40));
    if (ids.length) {
        out.selectedIds = ids;
    }
    return Object.keys(out).length ? out : undefined;
}

/** Appends to the trail (newest last), keeping only the last `max` entries; drops an exact repeat of the last one. */
export function pushTrail(trail: AssistantTrailEntry[], entry: AssistantTrailEntry, max = TRAIL_MAX): AssistantTrailEntry[] {
    const clean: AssistantTrailEntry = {t: entry.t, at: entry.at, label: clip(entry.label)};
    if (entry.detail) {
        clean.detail = clip(entry.detail);
    }
    if (!clean.label) {
        return trail;
    }
    const last = trail[trail.length - 1];
    if (last && last.t === clean.t && last.label === clean.label && last.detail === clean.detail) {
        return trail;
    }
    const next = trail.concat(clean);
    return next.length > max ? next.slice(next.length - max) : next;
}

/** Trail entry for a failed API call: method, path without query, status and the server's short message. */
export function apiErrorEntry(method: string, url: string, status: number, error: any, at: string): AssistantTrailEntry {
    let message = '';
    if (error && typeof error === 'object' && !(typeof Blob !== 'undefined' && error instanceof Blob)) {
        const fieldErrors: any[] = error.fieldErrors || error.violations;
        if (Array.isArray(fieldErrors) && fieldErrors.length) {
            message = fieldErrors
                .slice(0, 5)
                .map((fe) => `${fe && fe.field}: ${fe && fe.message}`)
                .join('; ');
        } else {
            message = error.detail || error.title || error.message || '';
        }
    } else if (typeof error === 'string') {
        const text = error.trim();
        message = text && !text.startsWith('<') && !text.startsWith('{') && text.length <= VALUE_MAX ? text : '';
    }
    const entry: AssistantTrailEntry = {
        t: 'apiError',
        at,
        label: `${(method || 'GET').toUpperCase()} ${pathOnly(url)} → ${status || 'network error'}`
    };
    if (message && typeof message === 'string') {
        entry.detail = message;
    }
    return entry;
}

/** UTF-8 size of a value's JSON. */
export function byteLength(value: any): number {
    const s = JSON.stringify(value);
    if (typeof TextEncoder !== 'undefined') {
        return new TextEncoder().encode(s).length;
    }
    return unescape(encodeURIComponent(s)).length;
}

/**
 * Trims the context until it fits `maxBytes` of JSON: oldest trail entries first, then selected ids, filters,
 * changed-field names, errors, breadcrumb and title. Returns a new object.
 */
export function capContext(ctx: AssistantScreenContext, maxBytes = CONTEXT_MAX_BYTES): AssistantScreenContext {
    const out: AssistantScreenContext = JSON.parse(JSON.stringify(ctx));
    const fits = () => byteLength(out) <= maxBytes;
    while (!fits() && out.trail && out.trail.length) {
        out.trail.shift();
    }
    if (out.trail && !out.trail.length) {
        delete out.trail;
    }
    const list = out.screen && out.screen.list;
    while (!fits() && list && list.selectedIds && list.selectedIds.length) {
        list.selectedIds.pop();
    }
    if (!fits() && list) {
        delete list.filters;
    }
    const form = out.screen && out.screen.form;
    while (!fits() && form && form.changed && form.changed.length) {
        form.changed.pop();
    }
    while (!fits() && form && form.errors && form.errors.length) {
        form.errors.pop();
    }
    if (!fits()) {
        delete out.breadcrumb;
    }
    if (!fits()) {
        delete out.title;
    }
    if (!fits() && out.screen) {
        out.screen = {kind: out.screen.kind, entity: out.screen.entity, id: out.screen.id};
    }
    if (!fits()) {
        out.url = clip(out.url, 100);
    }
    return out;
}

const KIND_WORD: {[k in AssistantScreenKind]: string} = {list: 'list', view: 'viewing', edit: 'editing', create: 'creating', other: ''};

/** Text of the context chip in the chat: "Haulier Order HO-2318 · editing", "Haulier Orders · list (3 selected)". */
export function contextChipLabel(ctx: AssistantScreenContext): string {
    if (!ctx) {
        return '';
    }
    const s = ctx.screen || {kind: 'other'};
    const entity = s.entity ? humanize(s.entity) : '';
    if (s.kind === 'list') {
        const selected = s.list && s.list.selectedIds ? s.list.selectedIds.length : 0;
        return clip(`${entity || ctx.title || 'List'} · list${selected ? ` (${selected} selected)` : ''}`, 80);
    }
    if (s.kind === 'view' || s.kind === 'edit' || s.kind === 'create') {
        // the record's name is the crumb that does not come from the URL itself; else "#id"
        const fromUrl = breadcrumbFromUrl(ctx.url || '');
        const named = (ctx.breadcrumb || []).filter((c) => fromUrl.indexOf(c) < 0).pop();
        const ref = s.kind === 'create' ? '' : named || (s.id ? '#' + s.id : '');
        const name = singular(entity) || ctx.title || 'Record';
        return clip(`${name}${ref ? ' ' + ref : ''} · ${KIND_WORD[s.kind]}`, 80);
    }
    // the home page has no breadcrumb; never show a bare "/" (CU-14zefhy0dgu D5)
    if (pathOnly(ctx.url || '/') === '/') {
        return 'Home';
    }
    const label = clip(ctx.title || (ctx.breadcrumb || []).join(' › ') || ctx.url, 80);
    return label && label !== '/' ? label : 'Home';
}

/** Prompts that fit the current screen (edit with errors → "Why can't I save?", view → "Summarise this", …). */
export function contextSuggestions(ctx: AssistantScreenContext): string[] {
    if (!ctx || !ctx.screen) {
        return [];
    }
    const s = ctx.screen;
    const thing = s.entity ? singular(humanize(s.entity)).toLowerCase() : 'record';
    const out: string[] = [];
    const lastError = (ctx.trail || []).filter((e) => e.t === 'apiError').pop();
    if ((s.kind === 'edit' || s.kind === 'create') && s.form && s.form.errors && s.form.errors.length) {
        out.push("Why can't I save?");
    } else if (s.kind === 'edit' || s.kind === 'create') {
        out.push(`What do I need to fill in on this ${thing}?`);
    }
    if (s.kind === 'view') {
        out.push('Summarise this ' + thing);
        out.push('What should I check next on this ' + thing + '?');
    }
    if (s.kind === 'list') {
        if (s.list && s.list.selectedIds && s.list.selectedIds.length > 1) {
            out.push('Compare the selected rows');
        } else if (s.list && s.list.selectedIds && s.list.selectedIds.length === 1) {
            out.push('Tell me about the selected row');
        }
        if (s.list && (s.list.filters || s.list.search)) {
            out.push('Explain what this filtered list shows');
        }
    }
    if (lastError) {
        out.unshift('What does the last error mean?');
    }
    return out.slice(0, 3);
}

export interface TextSegment {
    text: string;
    /** in-app path ("/runs/12") when this segment is a link */
    link?: string;
}

const LINK_RE =
    /\[([^\]\n]{1,120})\]\(([^)\s]{1,300})\)|(https?:\/\/[^\s<>()]+)|(^|[\s(])(\/[A-Za-z][A-Za-z0-9\-._~%/]+(?:\?[A-Za-z0-9\-._~%&=]*)?)/g;

/** Server paths that are never app routes (API, actuator, OAuth): never shown as in-app links. */
const NON_ROUTE_PREFIX = /^\/(api|management|oauth2)(\/|$|\?)/i;

/**
 * Top-level route segments of the app ("orders", "runs", ...) from a router config; empty paths and the
 * wildcard are left out. Used to decide which bare `/path` words in a reply are real app links.
 */
export function topLevelRoutes(config: Array<{path?: string; children?: any[]}>): string[] {
    const out = new Set<string>();
    const walk = (routes: Array<{path?: string; children?: any[]}>) => {
        (routes || []).forEach((r) => {
            const first = (r?.path || '').split('/')[0];
            if (first && first !== '**' && first.indexOf(':') !== 0) {
                out.add(first.toLowerCase());
            } else if (!first && r?.children) {
                // componentless layout route ("" with children): its children are top-level too
                walk(r.children);
            }
        });
    };
    walk(config);
    return Array.from(out);
}

/** Minimal shape of an Angular `Route` (duck-typed so tests need no Angular); `_loadedRoutes` = a loaded lazy module's routes. */
export interface RouteLike {
    path?: string;
    pathMatch?: string;
    children?: RouteLike[];
    loadChildren?: any;
    matcher?: any;
    _loadedRoutes?: RouteLike[];
}

/** 'yes' = a real route; 'no' = no route matches; 'unknown' = it would go into a lazy module that is not loaded yet. */
export type RouteMatch = 'yes' | 'no' | 'unknown';

/**
 * Whether an in-app path matches a route of the router config (CU-14zefhy0dgu D3): static segments must be equal,
 * `:param` takes one segment, and the wildcard `**` never counts (a made-up path must not look real). Lazy modules
 * that are already loaded are checked too; one that is not loaded yet gives 'unknown'.
 */
export function matchRoute(path: string, config: RouteLike[]): RouteMatch {
    const segments = pathOnly(path)
        .split('/')
        .filter(Boolean)
        .map((s) => {
            try {
                return decodeURIComponent(s);
            } catch (e) {
                return s;
            }
        });
    return matchSegments(segments, config || [], 0);
}

function matchSegments(segments: string[], routes: RouteLike[], depth: number): RouteMatch {
    if (depth > 12) {
        return 'unknown';
    }
    let unknown = false;
    for (const route of routes) {
        if (!route) {
            continue;
        }
        if (route.matcher) {
            unknown = true; // custom matcher: cannot tell without running it
            continue;
        }
        const parts = (route.path || '').split('/').filter(Boolean);
        if (route.path === '**' || parts.length > segments.length || !parts.every((p, i) => p.charAt(0) === ':' || p === segments[i])) {
            continue;
        }
        const rest = segments.slice(parts.length);
        if (route.pathMatch === 'full' && rest.length) {
            continue;
        }
        const children = route._loadedRoutes || route.children;
        if (children && children.length) {
            const inner = matchSegments(rest, children, depth + 1);
            if (inner === 'yes') {
                return 'yes';
            }
            unknown = unknown || inner === 'unknown';
            continue;
        }
        if (route.loadChildren) {
            unknown = true; // lazy module not loaded yet
            continue;
        }
        if (!rest.length) {
            return 'yes';
        }
    }
    return unknown ? 'unknown' : 'no';
}

/**
 * The lazy routes (`loadChildren`, not loaded yet) an in-app path would go into — the reason `matchRoute` says
 * 'unknown'. HM loads its modules lazily with no preloading, so the chat loads exactly these and checks the link again.
 */
export function unloadedLazyRoutes(path: string, config: RouteLike[]): RouteLike[] {
    const segments = pathOnly(path).split('/').filter(Boolean);
    const out: RouteLike[] = [];
    const walk = (rest: string[], routes: RouteLike[], depth: number) => {
        if (depth > 12) {
            return;
        }
        for (const route of routes || []) {
            if (!route || route.matcher || route.path === '**') {
                continue;
            }
            const parts = (route.path || '').split('/').filter(Boolean);
            if (parts.length > rest.length || !parts.every((p, i) => p.charAt(0) === ':' || p === safeDecode(rest[i]))) {
                continue;
            }
            const after = rest.slice(parts.length);
            if (route.pathMatch === 'full' && after.length) {
                continue;
            }
            const children = route._loadedRoutes || route.children;
            if (children && children.length) {
                walk(after, children, depth + 1);
            } else if (route.loadChildren && out.indexOf(route) < 0) {
                out.push(route);
            }
        }
    };
    walk(segments, config || [], 0);
    return out;
}

function safeDecode(s: string): string {
    try {
        return decodeURIComponent(s);
    } catch (e) {
        return s;
    }
}

/**
 * Record pages of the main HaulierMagic entities (each has `<base>/:id`, `:id/edit`, `new` and the list at `<base>`;
 * read from the route modules). HM loads its modules lazily, so these are always in the hint even before their module
 * is loaded; record pages of modules already loaded are added from the router config.
 */
export const APP_RECORD_BASES = [
    '/orders/haulier-orders',
    '/runs/runs',
    '/setup/organisations',
    '/setup/invoices',
    '/setup/credit-notes',
    '/setup/payments',
    '/setup/drivers',
    '/setup/vehicles',
    '/setup/trailers',
    '/setup/location-points',
    '/setup/users',
    '/manifests/manifests'
];

export const ROUTES_HINT_MAX_BYTES = 1024;
const ROUTES_HINT_LINE_MAX = 200;
const ROUTES_HINT_HEADER = 'view {base}/{id} · edit {base}/{id}/edit · new {base}/new · list {base}';

/** `<base>` paths of the loaded router config that have a `:id` child (record pages). */
export function recordBases(config: RouteLike[]): string[] {
    const out: string[] = [];
    const walk = (routes: RouteLike[], prefix: string, depth: number) => {
        (routes || []).forEach((r) => {
            const path = (r && r.path) || '';
            if (!r || depth > 8 || r.matcher || path === '**') {
                return;
            }
            // ":id" under "<prefix>" or a flat "x/:id" → record pages at "<prefix>" / "<prefix>/x"
            const m = /^(?:(.*)\/)?:id$/.exec(path);
            if (m) {
                const base = m[1] ? `${prefix}/${m[1]}` : prefix;
                if (base && out.indexOf(base) < 0) {
                    out.push(base);
                }
                return;
            }
            const children = r._loadedRoutes || r.children;
            if (children && path.indexOf(':') < 0) {
                walk(children, path ? `${prefix}/${path}` : prefix, depth + 1);
            }
        });
    };
    walk(config || [], '', 0);
    return out;
}
/**
 * The app-links hint sent as `context.routes` (wave contract, same shape as RM): `routes[0]` explains the path
 * pattern, `routes[1..]` are "<parent>/: base base …" groups — the current screen's group first, then the main
 * records, then record pages of loaded modules — each line ≤ 200 chars, the JSON ≤ 1 KB.
 */
export function routesHint(config: RouteLike[], currentUrl?: string): string[] {
    const current = pathOnly(currentUrl || '/');
    const bases: string[] = [];
    const add = (b: string) => {
        if (b && bases.indexOf(b) < 0) {
            bases.push(b);
        }
    };
    const all = APP_RECORD_BASES.concat(recordBases(config));
    all.filter((b) => current === b || current.indexOf(b + '/') === 0).forEach(add);
    all.forEach(add);
    const groups: string[] = [];
    const members: {[parent: string]: string[]} = {};
    bases.forEach((b) => {
        const cut = b.lastIndexOf('/');
        const parent = b.substring(0, cut + 1) || '/';
        if (!members[parent]) {
            members[parent] = [];
            groups.push(parent);
        }
        members[parent].push(b.substring(cut + 1));
    });
    const out = [ROUTES_HINT_HEADER];
    for (const parent of groups) {
        let line = parent + ':';
        for (const name of members[parent]) {
            if (line.length + 1 + name.length > ROUTES_HINT_LINE_MAX) {
                break;
            }
            line += ' ' + name;
        }
        if (new TextEncoder().encode(JSON.stringify(out.concat(line))).length > ROUTES_HINT_MAX_BYTES) {
            break;
        }
        out.push(line);
    }
    return out;
}

/**
 * In-app path for a link target, or null when it is not same-origin / not a plain path / a server path
 * (`/api/...`). When `roots` is given, the path's first segment must be one of them.
 */
export function inAppPath(target: string, origin: string, roots?: string[]): string | null {
    if (!target) {
        return null;
    }
    let t = target.trim().replace(/[.,;:!?]+$/, '');
    if (/^https?:\/\//i.test(t)) {
        if (!origin || t.toLowerCase().indexOf(origin.toLowerCase() + '/') !== 0) {
            return null;
        }
        t = t.substring(origin.length);
    }
    // same-origin path only: "/x", never "//host" or "/\host" (protocol-relative) or javascript:
    if (!/^\/[A-Za-z0-9]/.test(t) || t.indexOf('//') >= 0 || t.indexOf('\\') >= 0 || NON_ROUTE_PREFIX.test(t)) {
        return null;
    }
    if (roots) {
        const first = t.substring(1).split(/[/?#]/)[0].toLowerCase();
        if (roots.indexOf(first) < 0) {
            return null;
        }
    }
    return t;
}

/**
 * Splits an assistant reply into text and in-app link segments: markdown links `[label](/path)`, same-origin
 * absolute URLs and bare `/path` words. External links stay plain text. When `roots` (top-level app routes)
 * is given, a bare `/path` word becomes a link only if its first segment is one of them, so endpoint or
 * file paths named in a reply (`/home/...`) stay text. When `isRoute` is given, every kind of link must also pass it
 * (a real route of the app, see `matchRoute`); a made-up one stays plain text (a markdown link shows its label only).
 */
export function splitLinks(text: string, origin: string, roots?: string[], isRoute?: (path: string) => boolean): TextSegment[] {
    const out: TextSegment[] = [];
    if (!text) {
        return out;
    }
    const push = (seg: TextSegment) => {
        const last = out[out.length - 1];
        if (!seg.link && last && !last.link) {
            last.text += seg.text;
        } else if (seg.text) {
            out.push(seg);
        }
    };
    let pos = 0;
    let m: RegExpExecArray;
    LINK_RE.lastIndex = 0;
    while ((m = LINK_RE.exec(text))) {
        let start = m.index;
        let raw = m[0];
        let label: string;
        let target: string;
        if (m[1] !== undefined) {
            label = m[1];
            target = m[2];
        } else if (m[3] !== undefined) {
            target = m[3];
            label = null;
        } else {
            // bare path: keep the leading space/paren as text
            start += m[4].length;
            raw = m[5];
            target = m[5];
            label = null;
        }
        // trailing punctuation belongs to the sentence, not the link
        const trail = label === null ? (target.match(/[.,;:!?]+$/) || [''])[0] : '';
        if (trail) {
            target = target.substring(0, target.length - trail.length);
            raw = raw.substring(0, raw.length - trail.length);
        }
        const bare = m[1] === undefined && m[3] === undefined;
        const path = inAppPath(target, origin, bare ? roots : undefined);
        push({text: text.substring(pos, start)});
        if (path && isRoute && !isRoute(path)) {
            push({text: label !== null ? label : raw});
        } else if (path) {
            push({text: label !== null ? label : raw, link: path});
        } else {
            push({text: raw});
        }
        pos = start + raw.length;
    }
    push({text: text.substring(pos)});
    return out;
}
